import { useEffect, useState, type PropsWithChildren, type ReactNode } from "react";
import type { PrimaryView } from "./routing";
import { chain, chainId } from "../lib/wallet";
import { cn } from "./cn";
import { useIsDesktop } from "./media";
import { SectionRail } from "./SectionRail";
import { IsoWorld } from "./IsoWorld";
import { HeroStatsPanel } from "./HeroStatsPanel";
import { getLegalBundle, LEGAL_DOC_ORDER } from "../i18n/legal";
import { useI18n } from "../i18n";
import { isTouchViewport, scrollToGridAligned, watchGridAnchor } from "./gridAnchor";

/**
 * NarrativePage — WO#5 Neon-Editorial v2 (per Claudio's stealth-
 * aerospace visual reference).
 *
 * Vertically-scrolling narrative page. Same emotional progression as
 * v1 (desire → explore → understand → act) but the SKIN is now
 * editorial dark, teal/coral, near-black, asymmetric.
 *
 * ANTI-FAKE compliance:
 *   - No invented uptime, transactions/day, token price, node count.
 *   - Callouts on the hero isometric world map to CANONICAL facts
 *     only (Grid Zero nucleus, teal beam = zone edge, "cells
 *     allocated" pulls from /api/v1/grid/stats).
 *   - "ENTER THE GRID" NEVER auto-triggers SIWE. It scrolls the
 *     reader into the interactive Grid section.
 */
export interface NarrativePageProps {
  view: PrimaryView;
  gridSlot: ReactNode;
}

export function NarrativePage({ view, gridSlot }: NarrativePageProps) {
  const [active, setActive] = useState("hero");

  // Deep-link intent: /#purchase and /#grid → jump into the Grid section
  // (mode is already `select` on the outer shell for purchase). A bare root
  // hash ("") keeps the hero/home landing, so only an EXPLICIT #grid scrolls.
  // A hashchange listener covers SPA navigation (e.g. Dashboard "Back to Grid")
  // where the element already exists and the browser skips native anchoring.
  useEffect(() => {
    const scrollToGrid = () => window.setTimeout(scrollToGridAligned, 60);
    const hash = typeof window !== "undefined" ? window.location.hash.replace(/^#/, "") : "";
    if (view === "purchase" || hash === "grid" || hash.startsWith("grid?")) scrollToGrid();
    const onHash = () => {
      const h = window.location.hash.replace(/^#/, "");
      if (h === "grid" || h.startsWith("grid?")) scrollToGrid();
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [view]);

  // WO-VRD-18 — active-slide tracking (side dots) + first-view reveal.
  useEffect(() => {
    const ids = ["hero", "story", "grid", "roadmap", "cta"];
    const ratios = new Map<string, number>();
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const id = (e.target as HTMLElement).id;
          ratios.set(id, e.intersectionRatio);
          if (e.isIntersecting && e.intersectionRatio >= 0.4) {
            e.target.classList.add("in-view");
          }
        }
        let best = ids[0];
        let bestR = -1;
        for (const id of ids) {
          const r = ratios.get(id) ?? 0;
          if (r > bestR) {
            bestR = r;
            best = id;
          }
        }
        setActive(best);
      },
      { threshold: [0, 0.2, 0.4, 0.6, 0.8, 1] },
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  // WO-VRD-19 — full-screen Territory: hide the topbar while the grid
  // slide is the active one so the map gets an immersive full stage.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("grid-immersive", active === "grid");
    return () => root.classList.remove("grid-immersive");
  }, [active]);

  // Stage 10 — mobile: an upward page scroll on the immersive Grid brings the
  // topbar back (scrolling down hides it again), so it is always reachable.
  useEffect(() => {
    if (active !== "grid") return;
    const root = document.documentElement;
    const mobile = window.matchMedia("(max-width: 767px)");
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      if (Math.abs(y - lastY) < 4) return;
      root.classList.toggle("grid-nav-scrollup", mobile.matches && y < lastY);
      lastY = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      root.classList.remove("grid-nav-scrollup");
    };
  }, [active]);

  // Stage 12 — touch devices: keep the active Grid aligned across Safari
  // dynamic-bar resizes (Safari has no scroll anchoring).
  useEffect(() => {
    if (active !== "grid" || !isTouchViewport()) return;
    return watchGridAnchor();
  }, [active]);

  return (
    <div data-testid="narrative-page" className="w-full">
      <SlideDots active={active} />
      <div className="min-h-[calc(100dvh-56px)] flex flex-col justify-center snap-start snap-always scroll-mt-[calc(56px+env(safe-area-inset-top,0px))]">
        <HeroSection />
        <FeatureBand />
      </div>
      <StorySection />
      <GridSection>{gridSlot}</GridSection>
      <RoadmapSection />
      <CtaSection />
      <SiteFooter />
    </div>
  );
}

/**
 * SlideDots — WO-VRD-18 (Claudio). Fixed vertical navigation dots on the
 * right that highlight the active full-screen slide as the reader scrolls
 * (proximity snap). Desktop only. Clicking a dot smooth-scrolls to its
 * section. Pure presentation — no canonical/product logic.
 */
function SlideDots({ active }: { active: string }) {
  const { t } = useI18n();
  const [showLabel, setShowLabel] = useState(true);
  useEffect(() => {
    setShowLabel(true);
    const t = window.setTimeout(() => setShowLabel(false), 3000);
    return () => window.clearTimeout(t);
  }, [active]);
  const items = [
    { id: "hero", label: t("home.slidedots.overview") },
    { id: "story", label: t("home.slidedots.story") },
    { id: "grid", label: t("home.slidedots.territory") },
    { id: "roadmap", label: t("home.slidedots.roadmap") },
    { id: "cta", label: t("home.slidedots.enter") },
  ];
  return (
    <nav
      data-testid="slide-dots"
      aria-label={t("home.slidedots.aria")}
      className={cn(
        "hidden md:flex fixed right-5 top-1/2 -translate-y-1/2 z-topbar",
        "flex-col items-end gap-3.5 pointer-events-auto",
      )}
    >
      {items.map((it) => {
        const on = active === it.id;
        return (
          <button
            key={it.id}
            type="button"
            data-testid={`slide-dot-${it.id}`}
            data-active={on ? "true" : "false"}
            aria-label={it.label}
            aria-current={on ? "true" : undefined}
            onClick={() =>
              it.id === "grid"
                ? scrollToGridAligned()
                : document
                    .getElementById(it.id)
                    ?.scrollIntoView({ behavior: "smooth" })
            }
            className="group relative flex items-center gap-2"
          >
            <span
              className={cn(
                "font-mono text-[9px] tracking-[0.22em] uppercase",
                "transition-all duration-med",
                on ? "text-neon-teal" : "text-text-secondary",
                on && showLabel
                  ? "opacity-100"
                  : "opacity-0 group-hover:opacity-100",
              )}
            >
              {it.label}
            </span>
            <span
              aria-hidden
              className={cn(
                "block rounded-full transition-all duration-med",
                on
                  ? "h-2.5 w-2.5 bg-neon-teal shadow-glow-teal-sm"
                  : "h-1.5 w-1.5 bg-text-muted/50 group-hover:bg-text-secondary",
              )}
            />
          </button>
        );
      })}
    </nav>
  );
}

/* ═══════════════════════════ 01 · HERO ═══════════════════════════ */

function HeroSection() {
  const { t } = useI18n();
  const chainName = chain?.name ?? `Chain ${chainId}`;
  return (
    <section
      id="hero"
      data-testid="section-hero"
      className={cn(
        "relative flex flex-col justify-center",
        "py-6 md:py-8",
        "px-4 md:px-8 lg:px-12",
      )}
    >
      <div className="snap-reveal max-w-[1440px] mx-auto grid grid-cols-12 gap-x-6 gap-y-10 md:gap-x-8">
        {/* LEFT RAIL — "01" ticks + coral diamond */}
        <div className="hidden md:flex col-span-1 pt-6 justify-start">
          <SectionRail number="01" activeMarker side="left" />
        </div>

        {/* LEFT COL — editorial hero */}
        <div className="col-span-12 md:col-span-6 lg:col-span-5 flex flex-col justify-center min-h-0">
          {/* Eyebrow */}
          <div className="flex items-center justify-center md:justify-start gap-3">
            <span className="font-mono text-[11px] tracking-[0.28em] text-neon-teal">
              {t("home.hero.eyebrow")}
            </span>
          </div>

          {/* Title — "THE" over "GRID", marble textured, centred */}
          <h1
            data-testid="hero-headline"
            className={cn(
              "mt-6 md:mt-8 text-center",
              "font-hero uppercase select-none",
              "leading-[0.86] tracking-[0.01em]",
            )}
          >
            <span
              className={cn(
                "block hero-marble",
                "text-[19vw] md:text-[clamp(3.5rem,min(11vw,16.5vh),168px)]",
              )}
            >
              THE
            </span>
            <span
              className={cn(
                "block hero-marble",
                "text-[19vw] md:text-[clamp(3.5rem,min(11vw,16.5vh),168px)]",
                "-mt-[0.10em]",
              )}
            >
              GRID
            </span>
          </h1>

          {/* Paragraph */}
          <p
            className={cn(
              "mt-6 md:mt-8 max-w-[460px]",
              "font-display text-[13.5px] md:text-[15px] leading-[1.55]",
              "text-text-secondary",
            )}
          >
            {t("home.hero.paragraph")}
          </p>

          {/* CTA + scroll indicator */}
          <div className="mt-8 md:mt-10 flex flex-col md:flex-row items-center md:items-start gap-5 md:gap-8">
            <a
              data-testid="hero-cta-enter"
              href="#grid"
              onClick={(e) => {
                e.preventDefault();
                scrollToGridAligned();
              }}
              className={cn(
                "group inline-flex items-center gap-3 h-10 px-5",
                "font-display text-[11.5px] font-semibold tracking-[0.24em] uppercase",
                "text-text-primary no-underline",
                "pill-gradient-border",
                "transition-transform duration-fast",
                "hover:translate-y-[-1px]",
              )}
            >
              {t("home.hero.enterGrid")}
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
                <path
                  d="M3 6h6m0 0L6 3m3 3L6 9"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>

            <a
              href="#story"
              onClick={(e) => {
                e.preventDefault();
                document
                  .getElementById("story")
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
              className={cn(
                "group inline-flex items-center gap-3 no-underline",
                "font-display text-[10.5px] font-semibold tracking-[0.26em] uppercase",
                "text-text-secondary hover:text-text-primary transition-colors duration-fast",
              )}
              aria-label={t("home.hero.scrollAria")}
            >
              <span
                aria-hidden
                className="inline-flex items-center justify-center h-7 w-4 rounded-full border border-border-strong"
              >
                <span className="block h-1.5 w-px bg-neon-teal mt-1 group-hover:mt-3 transition-all duration-slow" />
              </span>
              <span className="flex flex-col">
                {t("home.hero.scrollLine1")}
                <span>{t("home.hero.scrollLine2")}</span>
              </span>
              <span aria-hidden className="text-neon-teal text-[14px] leading-none">↓</span>
            </a>
          </div>
        </div>

        {/* CENTER — isometric world + callouts */}
        <div className="col-span-12 md:col-span-5 lg:col-span-4 relative min-h-[420px] md:min-h-[clamp(360px,50vh,540px)]">
          <IsoWorld className="absolute inset-0" />

          {/* Callout: GRID ZERO (top) → maps to nucleus */}
          <Callout
            className="left-[48%] top-[18%]"
            testid="callout-gridzero"
            title={t("home.callouts.gridZero.title")}
            body={t("home.callouts.gridZero.body")}
            leader={{ from: "left-1/2 top-full", to: "50%,66%" }}
          />

          {/* Callout: DATA LATTICE (left) → maps to teal ridge */}
          <Callout
            className="left-[8%] bottom-[24%]"
            testid="callout-lattice"
            title={t("home.callouts.lattice.title")}
            body={t("home.callouts.lattice.body")}
            leader={{ from: "right-0 top-1/2", to: "56%,72%" }}
          />

          {/* Live stat callout — real from /api/v1/grid/stats via HeroStatsPanel data */}
          <LiveCellCallout />
        </div>

        {/* RIGHT COL — Network Status panel (real data) */}
        <div className="col-span-12 lg:col-span-2 flex justify-center md:justify-end">
          <div className="w-full max-w-[360px] md:max-w-[300px] lg:max-w-none mx-auto md:mx-0 mt-2 lg:mt-4">
            <HeroStatsPanel />
            <div className="mt-4 hidden lg:block">
              <MiniInstrument chain={chainName} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Callout — the leader-line annotation box from Claudio's mock.
 * The leader is drawn as an absolute-positioned SVG so it can point
 * from the box edge to any world coordinate expressed as `x%,y%`
 * of the parent container.
 */
function Callout({
  className,
  testid,
  title,
  body,
  leader,
}: {
  className?: string;
  testid?: string;
  title: string;
  body?: string;
  leader?: { from: string; to: string };
}) {
  return (
    <div
      className={cn("absolute z-overlay", className)}
      data-testid={testid}
      style={{ pointerEvents: "none" }}
    >
      <div className="relative">
        <div className="callout-box px-2.5 py-1.5">
          <div className="font-display text-[10px] font-semibold tracking-[0.20em] uppercase text-neon-teal whitespace-nowrap">
            {title} <span className="text-text-muted">—</span>
          </div>
          {body && (
            <div className="font-display text-[11px] text-text-secondary whitespace-nowrap">
              {body}
            </div>
          )}
        </div>
        {leader && (
          <svg
            className="absolute -z-10 overflow-visible"
            width="140"
            height="120"
            style={{ left: "50%", top: "100%", transform: "translate(-6px, -2px)" }}
            aria-hidden
          >
            <line
              x1="0"
              y1="0"
              x2="80"
              y2="70"
              stroke="#2DB6F0"
              strokeOpacity="0.55"
              strokeWidth="1"
            />
            <circle cx="80" cy="70" r="2" fill="#2DB6F0" />
          </svg>
        )}
      </div>
    </div>
  );
}

function LiveCellCallout() {
  const { t } = useI18n();
  return (
    <div
      data-testid="callout-cells"
      className="absolute right-[10%] bottom-[10%] z-overlay"
      style={{ pointerEvents: "none" }}
    >
      <div className="callout-box px-3 py-2 flex items-center gap-3">
        <span aria-hidden>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <rect x="2" y="2" width="16" height="16" rx="2" stroke="#2DB6F0" strokeOpacity="0.8"/>
            <path d="M6 6h8v8H6z" fill="#2DB6F0" opacity="0.24"/>
          </svg>
        </span>
        <div>
          <div className="font-display text-[9.5px] font-semibold tracking-[0.22em] uppercase text-neon-teal">
            {t("home.callouts.cells.label")}
          </div>
          <div className="font-hero text-[16px] text-text-primary leading-tight">
            1,000,000
          </div>
        </div>
        {/* mini bar chart chip */}
        <span aria-hidden className="ml-1 flex items-end gap-[2px] h-4">
          {[3, 6, 4, 8, 5, 9, 7].map((h, i) => (
            <span
              key={i}
              className="w-[2px] bg-neon-teal"
              style={{ height: `${h * 2}px`, opacity: 0.45 + i * 0.06 }}
            />
          ))}
        </span>
      </div>
    </div>
  );
}

function MiniInstrument({ chain }: { chain: string }) {
  const { t } = useI18n();
  return (
    <div
      data-testid="hero-instrument"
      className={cn("neon-glass px-4 py-3", "font-mono text-[11px]")}
    >
      <div className="text-neon-teal tracking-[0.18em] uppercase font-display text-[9.5px] font-semibold mb-2">
        {t("home.instrument.title")}
      </div>
      <ul className="space-y-1 text-text-secondary">
        <li><span className="text-text-muted">{t("home.instrument.labels.grid")}</span>&nbsp;&nbsp;1000 × 1000</li>
        <li><span className="text-text-muted">{t("home.instrument.labels.chain")}</span>&nbsp;<span className="text-neon-teal">{chain}</span></li>
        <li><span className="text-text-muted">{t("home.instrument.labels.maxZoom")}</span>&nbsp;10×</li>
        <li><span className="text-text-muted">{t("home.instrument.labels.auth")}</span>&nbsp;SIWE · 1271 · 6492</li>
      </ul>
    </div>
  );
}

/* ═══════════════════════ FEATURE BAND ═══════════════════════════ */

const FEATURE_ICONS = [
  (
    <svg key="feature-icon-0" width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="2" y="2" width="14" height="14" rx="1.5" stroke="#2DB6F0" strokeOpacity="0.9"/>
      <path d="M2 9h14M9 2v14" stroke="#2DB6F0" strokeOpacity="0.55"/>
    </svg>
  ),
  (
    <svg key="feature-icon-1" width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="7" stroke="#2DB6F0" strokeOpacity="0.9"/>
      <path d="M4 9h10" stroke="#FF4757" strokeWidth="1.2"/>
    </svg>
  ),
  (
    <svg key="feature-icon-2" width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="6" stroke="#2DB6F0" strokeOpacity="0.9"/>
      <circle cx="9" cy="9" r="2.2" fill="#2DB6F0" fillOpacity="0.35"/>
    </svg>
  ),
  (
    <svg key="feature-icon-3" width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="3" y="6" width="12" height="9" rx="1.5" stroke="#2DB6F0" strokeOpacity="0.9"/>
      <path d="M6 6V4a3 3 0 016 0v2" stroke="#2DB6F0" strokeOpacity="0.55"/>
    </svg>
  ),
];

function FeatureBand() {
  const { dict } = useI18n();
  return (
    <section
      data-testid="section-features"
      className={cn(
        "relative border-y border-border-divider",
        "bg-obsidian/60 backdrop-blur",
        "py-6 md:py-7",
      )}
    >
      <div className="max-w-[1440px] mx-auto px-4 md:px-8 lg:px-12 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
        {dict.home.features.map((f, i) => (
          <div key={f.title} className="flex items-start gap-3">
            <span className="mt-0.5 shrink-0">{FEATURE_ICONS[i]}</span>
            <div>
              <div className="font-display text-[12px] font-semibold tracking-[0.14em] uppercase text-text-primary">
                {f.title}
              </div>
              <div className="mt-0.5 font-display text-[12px] text-text-secondary leading-[1.4]">
                {f.body}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ═══════════════════════ 02 · STORY ═════════════════════════════ */

function StorySection() {
  const { t, dict } = useI18n();
  return (
    <section
      id="story"
      data-testid="section-story"
      className={cn(
        "relative min-h-[calc(100dvh-56px)] flex items-center snap-start snap-always scroll-mt-[calc(56px+env(safe-area-inset-top,0px))]",
        "px-4 md:px-8 lg:px-12 py-[clamp(1.25rem,4vh,4.5rem)]",
      )}
    >
      <div className="snap-reveal max-w-[1440px] mx-auto grid grid-cols-12 gap-x-8 gap-y-10 items-center">
        <div className="hidden md:flex col-span-1 justify-start">
          <SectionRail number="02" side="left" />
        </div>

        <div className="col-span-12 md:col-span-6 lg:col-span-6">
          <div className="flex items-center justify-center md:justify-start gap-3">
            <span aria-hidden className="block h-px w-8 bg-text-muted" />
            <span className="font-mono text-[10.5px] tracking-[0.28em] uppercase text-text-secondary">
              {t("home.story.eyebrow")}
            </span>
          </div>
          <h2
            className={cn(
              "mt-6 font-hero uppercase text-text-primary",
              "text-[10.5vw] md:text-[clamp(2.5rem,min(6vw,9vh),92px)]",
              "leading-[0.92] tracking-[0.01em]",
              "max-w-[820px]",
              "text-center md:text-left",
            )}
          >
            <span className="break-words md:whitespace-normal">
              {dict.home.story.titleLine1}
              <span className="md:hidden"> </span>
              <br className="hidden md:inline" />
              {dict.home.story.titleLine2}
            </span>
            <br />
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(90deg,#2DB6F0 0%,#74D4FF 55%,#118FD4 100%)",
              }}
            >
              {dict.home.story.titleHighlight}
            </span>
          </h2>
          <p className="mt-6 max-w-[560px] mx-auto md:mx-0 text-center md:text-left font-display text-[15px] leading-[1.65] text-text-secondary">
            {t("home.story.paragraph")}
          </p>
        </div>

        <div className="col-span-12 md:col-span-5 lg:col-span-5 relative min-h-[280px] md:min-h-[clamp(240px,33vh,360px)]">
          <ConnectionArtwork />
        </div>
      </div>
    </section>
  );
}

function ConnectionArtwork() {
  return (
    <svg
      viewBox="0 0 500 360"
      className="absolute inset-0 w-full h-full"
      aria-hidden
    >
      <defs>
        <radialGradient id="core" cx="50%" cy="50%" r="50%">
          <stop offset="0" stopColor="#2DB6F0" stopOpacity="0.85"/>
          <stop offset="1" stopColor="#2DB6F0" stopOpacity="0"/>
        </radialGradient>
      </defs>
      <circle cx="250" cy="180" r="130" fill="url(#core)" opacity="0.65"/>
      {/* Radiating lines */}
      {Array.from({ length: 24 }).map((_, i) => {
        const angle = (i * Math.PI * 2) / 24;
        const x2 = 250 + Math.cos(angle) * 240;
        const y2 = 180 + Math.sin(angle) * 240;
        return (
          <line
            key={i}
            x1={250}
            y1={180}
            x2={x2}
            y2={y2}
            stroke="#2DB6F0"
            strokeOpacity={0.35 - (i % 3) * 0.08}
            strokeWidth={0.8}
          />
        );
      })}
      {/* Concentric rings */}
      {[40, 80, 120, 170, 220].map((r, i) => (
        <circle
          key={r}
          cx="250"
          cy="180"
          r={r}
          fill="none"
          stroke="#2DB6F0"
          strokeOpacity={0.35 - i * 0.05}
          strokeWidth={0.6}
        />
      ))}
      <circle cx="250" cy="180" r="6" fill="#74D4FF"/>
      <circle cx="250" cy="180" r="14" fill="none" stroke="#74D4FF" strokeWidth="1"/>
    </svg>
  );
}

/* ═══════════════════════ 03 · TERRITORY (Grid) ═══════════════════ */

function GridSection({ children }: PropsWithChildren) {
  return (
    <section
      id="grid"
      data-testid="section-grid"
      className={cn(
        "relative w-full",
        // WO-VRD-7 §FULL-VIEWPORT (Claudio) — the Territory view must fill
        // the whole screen so the next narrative section never peeks in at
        // the bottom. The topbar is sticky (56px) and overlays the top of
        // this section, so a full 100dvh section bottoms out exactly at the
        // viewport edge. Only THIS section changes; the rest of the
        // narrative landing is untouched.
        // MACRO #31 — the 640px floor applies ONLY when the viewport is tall
        // enough; on short screens (small phones / landscape) the Grid fits the
        // dynamic viewport instead of overflowing below the fold.
        "h-[100dvh] [@media(min-height:640px)]:min-h-[640px] snap-start snap-always",
        "border-y border-border-divider",
        "overflow-hidden bg-[#05080d]",
      )}
    >
      {/* WO#5 v3 §SUBSTRATO — technological circuit substrate under
          / around the canonical territory. Reads as "the Grid is
          incrusted into a larger technological plate". Pure
          decoration: nothing in this layer is canonical territory,
          buyable parcel, ownership, or backend state. Absolutely
          static SVG data-URI (no per-frame JS). */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          backgroundImage: `url("data:image/svg+xml;utf8,${encodeURIComponent(SUBSTRATE_SVG)}")`,
          backgroundRepeat: "repeat",
          backgroundSize: "480px 480px",
          opacity: 0.9,
          maskImage:
            "radial-gradient(ellipse at 50% 50%, rgba(0,0,0,0.95) 65%, rgba(0,0,0,0.78) 100%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at 50% 50%, rgba(0,0,0,0.95) 65%, rgba(0,0,0,0.78) 100%)",
        }}
      />

      {/* Ambient atmosphere behind the canvas — generic teal
          peripheral wash for cinematic depth. There is NO coral
          centered gradient here: the Grid Zero coral gravity is
          rendered inside the canvas from canonical manifest
          regions (see renderer.ts §5 §GRID-ZERO-AUTHORITY), so
          when sale authority is unavailable the section falls
          back to a neutral teal ambience with no false Grid Zero
          signal. Fixed relative to the section so it never
          scrolls with the canvas. GPU-cheap. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 100%, rgba(45, 182, 240,0.06) 0%, transparent 55%), radial-gradient(ellipse at 50% 0%, rgba(45, 182, 240,0.04) 0%, transparent 55%)",
        }}
      />

      {/* Territory precision label — now lives in the GridWorkspace bottom
          Territory toolbar on BOTH mobile & desktop (MACRO #31 PAGE 3 parity),
          so the standalone mobile label is no longer needed. */}

      {/* World-frame corner ticks (top row) — decorative precision */}
      <FrameTicks />

      {/* Cosmic backdrop image behind the grid (page background, ONLY behind
          the Territory section). Darkened for harmony so the grid plane stays
          the focal point. The canvas is transparent outside the plane, so this
          shows in the surrounding space around the grid. Pure decoration. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          backgroundImage: "url('/cosmic/page-backdrop.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          filter: "brightness(0.7)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 46%, rgba(2,5,12,0.32) 0%, rgba(2,5,12,0.60) 58%, rgba(1,3,8,0.84) 100%)",
        }}
      />

      <div className="relative z-canvas h-full w-full">{children}</div>
    </section>
  );
}

/**
 * FrameTicks — subtle brackets in the four corners of the Territory
 * section. Signals that the Grid inside is a precious, finite object,
 * not a page fragment. Zero product logic.
 */
function FrameTicks() {
  // MACRO #31 PAGE 3 (Claudio) — the four corner brackets are hidden on mobile
  // (desktop keeps them as the "precious finite object" frame).
  const common =
    "absolute h-6 w-6 border-neon-teal/60 z-hud pointer-events-none hidden md:block";
  return (
    <>
      <span className={cn(common, "top-2 left-2 border-t border-l")} />
      <span className={cn(common, "top-2 right-2 border-t border-r")} />
      <span className={cn(common, "bottom-2 left-2 border-b border-l")} />
      <span className={cn(common, "bottom-2 right-2 border-b border-r")} />
    </>
  );
}

/* ═══════════════════════ 04 · ROADMAP ════════════════════════════ */

export const ROADMAP = [
  { phase: "Phase 0", status: "live" as const },
  { phase: "Phase 1", status: "live" as const },
  { phase: "Phase 2", status: "next" as const },
  { phase: "Phase 3", status: "later" as const },
];

function RoadmapSection() {
  const { t, dict } = useI18n();
  return (
    <section
      id="roadmap"
      data-testid="section-roadmap"
      className={cn(
        "relative min-h-[calc(100dvh-56px)] flex items-center snap-start snap-always scroll-mt-[calc(56px+env(safe-area-inset-top,0px))]",
        "px-4 md:px-8 lg:px-12 py-[clamp(1.25rem,4vh,4.5rem)]",
        "border-t border-border-divider",
      )}
    >
      <div className="snap-reveal max-w-[1440px] mx-auto grid grid-cols-12 gap-x-8 gap-y-[clamp(1.5rem,3vh,3.5rem)]">
        <div className="hidden md:flex col-span-1 justify-start">
          <SectionRail number="04" side="left" />
        </div>

        <div className="col-span-12 md:col-span-11">
          <div className="flex items-center justify-center md:justify-start gap-3">
            <span aria-hidden className="block h-px w-8 bg-text-muted" />
            <span className="font-mono text-[10.5px] tracking-[0.28em] uppercase text-text-secondary">
              {t("home.roadmap.eyebrow")}
            </span>
            <span aria-hidden className="h-3 w-px bg-border-divider" />
            <span
              data-testid="roadmap-live-count"
              className="inline-flex items-center gap-1.5 font-mono text-[10.5px] tracking-[0.2em] uppercase text-neon-teal"
            >
              <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-neon-teal dot-pulse" />
              {t("home.roadmap.phasesLive", {
                live: ROADMAP.filter((r) => r.status === "live").length,
                total: ROADMAP.length,
              })}
            </span>
          </div>
          <h3
            className={cn(
              "mt-6 font-hero uppercase text-text-primary",
              "text-[8.5vw] md:text-[clamp(2.25rem,min(5vw,7.5vh),76px)]",
              "leading-[0.94] tracking-[0.01em]",
              "max-w-[900px]",
              "text-center md:text-left",
            )}
          >
            {(() => {
              const title = t("home.roadmap.title");
              const i = title.indexOf(". ");
              if (i === -1) return title;
              return (
                <>
                  {title.slice(0, i + 1)}
                  <br className="md:hidden" />
                  <span className="hidden md:inline">{" "}</span>
                  {title.slice(i + 2)}
                </>
              );
            })()}
          </h3>
          <p className="mt-6 max-w-[640px] mx-auto md:mx-0 text-center md:text-left font-display text-[14.5px] leading-[1.65] text-text-secondary">
            {t("home.roadmap.paragraph")}
          </p>

          <div className="mt-[clamp(1.5rem,3.5vh,3.5rem)] grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {ROADMAP.map((r, i) => {
              const p = dict.home.roadmap.phases[i];
              return (
              <div
                key={r.phase}
                data-testid={`roadmap-phase-${r.phase.toLowerCase().replace(" ", "-")}`}
                className={cn(
                  "neon-glass p-6 md:p-7 flex flex-col gap-4",
                  r.status === "live" && "shadow-[0_0_0_1px_var(--border-strong)] shadow-glow-teal-md",
                )}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10.5px] font-semibold tracking-[0.24em] uppercase text-text-muted">
                    {t("home.roadmap.phaseLabel")} {r.phase.split(" ")[1]}
                  </span>
                  <StatusPill status={r.status} />
                </div>
                <div className="font-hero text-[26px] uppercase text-text-primary leading-tight">
                  {p.title}
                </div>
                <ul className="space-y-1.5 list-none p-0 m-0">
                  {p.items.map((it) => (
                    <li
                      key={it}
                      className="flex items-start gap-2 font-display text-[13px] text-text-secondary leading-[1.5]"
                    >
                      {r.status === "live" ? (
                        <svg
                          aria-hidden
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          className="mt-[3px] shrink-0 text-neon-teal"
                        >
                          <path
                            d="M2.4 6.2 4.8 8.6 9.6 3.4"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : (
                        <span
                          aria-hidden
                          className="mt-[6px] h-1 w-1 shrink-0 rounded-[1px] bg-border-strong"
                        />
                      )}
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function StatusPill({ status }: { status: "live" | "next" | "later" }) {
  const { t } = useI18n();
  const map = {
    live: { key: "live", cls: "text-neon-teal border-border-accent" },
    next: { key: "next", cls: "text-neon-coral border-border-coral" },
    later: { key: "later", cls: "text-text-muted border-border-soft" },
  } as const;
  const m = map[status];
  const text = t(`home.roadmap.status.${m.key}`);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm",
        "font-mono text-[10px] tracking-[0.18em] uppercase",
        "bg-obsidian/60 border",
        m.cls,
      )}
    >
      {status === "live" && (
        <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-neon-teal dot-pulse" />
      )}
      {text}
    </span>
  );
}

/* ─── Substrate SVG (decorative circuit-trace atmosphere) ──────
 * Zero canonical semantics. This is texture, not territory. */
const SUBSTRATE_SVG = `
<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 480 480'>
  <defs>
    <linearGradient id='t' x1='0' y1='0' x2='1' y2='0'>
      <stop offset='0'  stop-color='#2DB6F0' stop-opacity='0'/>
      <stop offset='0.5' stop-color='#2DB6F0' stop-opacity='0.55'/>
      <stop offset='1'  stop-color='#2DB6F0' stop-opacity='0'/>
    </linearGradient>
    <linearGradient id='c' x1='0' y1='0' x2='0' y2='1'>
      <stop offset='0'  stop-color='#FF4757' stop-opacity='0'/>
      <stop offset='0.5' stop-color='#FF4757' stop-opacity='0.55'/>
      <stop offset='1'  stop-color='#FF4757' stop-opacity='0'/>
    </linearGradient>
  </defs>
  <g fill='none' stroke='rgba(45, 182, 240,0.14)' stroke-width='1'>
    <path d='M0 60 L 200 60 L 220 80 L 480 80'/>
    <path d='M0 140 L 120 140 L 140 160 L 380 160 L 400 180 L 480 180'/>
    <path d='M0 240 L 60 240 L 80 260 L 260 260 L 280 280 L 480 280'/>
    <path d='M0 340 L 180 340 L 200 360 L 480 360'/>
    <path d='M0 420 L 220 420 L 240 440 L 460 440'/>
    <path d='M60 0 L 60 100 L 80 120 L 80 260'/>
    <path d='M180 0 L 180 60 L 200 80 L 200 220'/>
    <path d='M300 0 L 300 100 L 320 120 L 320 320'/>
    <path d='M420 0 L 420 180 L 400 200 L 400 340'/>
  </g>
  <!-- node dots -->
  <g fill='#2DB6F0'>
    <circle cx='60'  cy='60'  r='1.6'/>
    <circle cx='220' cy='80'  r='1.6'/>
    <circle cx='140' cy='160' r='1.6'/>
    <circle cx='400' cy='180' r='1.6'/>
    <circle cx='80'  cy='260' r='1.6'/>
    <circle cx='280' cy='280' r='1.6'/>
    <circle cx='200' cy='360' r='1.6'/>
    <circle cx='240' cy='440' r='1.6'/>
    <circle cx='320' cy='120' r='1.4'/>
  </g>
  <g fill='#FF4757' opacity='0.7'>
    <circle cx='300' cy='320' r='1.6'/>
    <circle cx='400' cy='340' r='1.6'/>
  </g>
  <!-- data flowing lines (short bright segments over the traces) -->
  <line x1='140' y1='60' x2='210' y2='60' stroke='url(#t)' stroke-width='1'/>
  <line x1='120' y1='340' x2='190' y2='340' stroke='url(#t)' stroke-width='1'/>
  <line x1='300' y1='60' x2='300' y2='90' stroke='url(#c)' stroke-width='1'/>
  <line x1='420' y1='120' x2='420' y2='150' stroke='url(#c)' stroke-width='1'/>
  <!-- faint micro-panel outlines -->
  <g fill='none' stroke='rgba(45, 182, 240,0.09)' stroke-width='1'>
    <rect x='36' y='96' width='58' height='20' rx='2'/>
    <rect x='250' y='200' width='68' height='20' rx='2'/>
    <rect x='120' y='380' width='58' height='20' rx='2'/>
    <rect x='340' y='60' width='68' height='20' rx='2'/>
  </g>
</svg>`;

/* ═══════════════════════ 05 · CTA ═══════════════════════════════ */

function CtaSection() {
  const { t, dict } = useI18n();
  return (
    <section
      id="cta"
      data-testid="section-cta"
      className={cn(
        "relative min-h-[calc(100dvh-56px)] snap-start snap-always scroll-mt-[calc(56px+env(safe-area-inset-top,0px))]",
        "px-4 md:px-8 lg:px-12 py-[clamp(1.25rem,4vh,4.5rem)]",
        "border-t border-border-divider",
        "flex items-center overflow-hidden",
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 left-1/2 -translate-x-1/2 h-[520px] w-[720px] rounded-full"
        style={{
          background:
            "radial-gradient(closest-side, rgba(255,71,87,0.20) 0%, transparent 70%)",
        }}
      />
      <div className="snap-reveal relative z-overlay max-w-[1440px] mx-auto w-full grid grid-cols-12 gap-x-8">
        <div className="hidden md:flex col-span-1 justify-start">
          <SectionRail number="05" side="left" />
        </div>
        <div className="col-span-12 md:col-span-11">
          <div className="flex items-center justify-center md:justify-start gap-3">
            <span aria-hidden className="block h-px w-8 bg-text-muted" />
            <span className="font-mono text-[10.5px] tracking-[0.28em] uppercase text-text-secondary">
              {t("home.cta.eyebrow")}
            </span>
          </div>
          <h3
            className={cn(
              "mt-6 font-hero uppercase text-text-primary",
              "text-[10.5vw] md:text-[clamp(2.5rem,min(6vw,10vh),92px)]",
              "leading-[0.92] tracking-[0.01em]",
              "max-w-[900px]",
              "text-center md:text-left",
            )}
          >
            {dict.home.cta.titlePrefix}
            <br className="md:hidden" />
            <span className="hidden md:inline">{" "}</span>
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(90deg,#FF6B76 0%,#FF4757 55%,#FF3040 100%)",
              }}
            >
              {dict.home.cta.titleHighlight}
            </span>
            .
          </h3>
          <p className="mt-6 max-w-[640px] mx-auto md:mx-0 text-center md:text-left font-display text-[15px] leading-[1.65] text-text-secondary">
            {t("home.cta.paragraph")}
          </p>

          <div className="mt-[clamp(1.5rem,4vh,3rem)] flex flex-col md:flex-row items-center md:items-stretch gap-4">
            <a
              data-testid="cta-return-grid"
              href="#grid"
              onClick={(e) => {
                e.preventDefault();
                scrollToGridAligned();
              }}
              className={cn(
                "facet-btn h-10 px-5 no-underline",
                "text-[11.5px] font-semibold tracking-[0.24em]",
              )}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
                <rect x="2" y="2" width="12" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
                <path d="M2 6.5h12M6.5 2v12" stroke="currentColor" strokeWidth="1" opacity="0.6" />
              </svg>
              {t("home.cta.exploreGrid")}
            </a>
            <a
              data-testid="cta-signin"
              href="#auth"
              className={cn(
                "inline-flex items-center gap-3 h-10 px-5",
                "font-display text-[11.5px] font-semibold tracking-[0.24em] uppercase",
                "text-text-primary no-underline",
                "pill-gradient-border",
              )}
            >
              {t("home.cta.signIn")}
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
                <path d="M3 6h6m0 0L6 3m3 3L6 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────── Footer ─────────────────────────── */

function SiteFooter() {
  const { t, locale } = useI18n();
  const isDesktop = useIsDesktop();
  const [legalOpen, setLegalOpen] = useState(false);
  const bundle = getLegalBundle(locale);

  // Desktop only: the inline "Legal & Policies" disclosure expands DOWNWARD at
  // the very bottom of the page. On open, smoothly scroll to the document end so
  // the freshly revealed catalogue is in view — never jump the reader back up
  // (Acceptance Fix — Claudio).
  useEffect(() => {
    if (!isDesktop || !legalOpen) return;
    const id = requestAnimationFrame(() => {
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
    });
    return () => cancelAnimationFrame(id);
  }, [isDesktop, legalOpen]);

  return (
    <footer
      data-testid="site-footer"
      className={cn(
        "relative border-t border-border-divider",
        "px-4 md:px-8 lg:px-12 py-8",
      )}
    >
      <div className="max-w-[1440px] mx-auto flex flex-col items-center gap-y-2.5 text-center text-[11px] font-mono text-text-muted md:flex-row md:flex-wrap md:items-center md:gap-x-8 md:gap-y-3 md:text-left">
        <span className="inline-flex items-center gap-2">
          <span aria-hidden className="h-2 w-2 rounded-[1px] bg-neon-teal" />
          THE GRID · V1.0 Alpha
        </span>
        <span aria-hidden className="hidden md:block h-3 w-px bg-border-divider" />
        <span>{chain?.name ?? `Chain ${chainId}`}</span>
        <span aria-hidden className="hidden md:block h-3 w-px bg-border-divider" />
        <span className="uppercase tracking-[0.2em]">{t("home.footer.walletAuth")}</span>
        {/* Desktop: the Legal & Policies badge sits right beside the wallet-auth
            chip. Mobile keeps the standalone disclosure below (unchanged). */}
        {isDesktop && (
          <>
            <span aria-hidden className="hidden md:block h-3 w-px bg-border-divider" />
            <button
              type="button"
              data-testid="footer-legal-toggle"
              aria-expanded={legalOpen}
              aria-controls="footer-legal-nav"
              onClick={() => setLegalOpen((v) => !v)}
              className={cn(
                "group inline-flex items-center gap-2 rounded-pill border border-border-soft",
                "px-3.5 py-2 min-h-[36px]",
                "font-mono text-[10px] uppercase tracking-[0.22em] text-text-secondary",
                "transition-colors hover:text-text-primary hover:border-border-strong",
              )}
            >
              <span aria-hidden className="h-1.5 w-1.5 rotate-45 bg-neon-teal/70" />
              {t("home.footer.legalToggle")}
              <svg
                width="11"
                height="11"
                viewBox="0 0 12 12"
                fill="none"
                aria-hidden
                className={cn("transition-transform duration-200", legalOpen ? "rotate-180" : "rotate-0")}
              >
                <path d="M2.5 4.5 6 8l3.5-3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </>
        )}
        <span className="text-text-faint md:ml-auto">
          {t("home.footer.territoryData")}
        </span>
      </div>

      {/* Desktop: expandable legal catalogue, auto-scrolled into view on open. */}
      {isDesktop && (
        <div className="max-w-[1440px] mx-auto">
          <nav
            id="footer-legal-nav"
            data-testid="footer-legal-nav"
            aria-label={t("home.footer.legalAria")}
            hidden={!legalOpen}
            className={cn(
              legalOpen ? "grid" : "hidden",
              "mt-4 w-full max-w-[760px]",
              "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4",
              "gap-x-5 gap-y-1 justify-items-start",
            )}
          >
            {LEGAL_DOC_ORDER.map((id) => (
              <a
                key={id}
                data-testid={`footer-legal-${id}`}
                href={`#legal/${id}`}
                className={cn(
                  "inline-flex items-center min-h-[36px] no-underline",
                  "font-mono text-[10px] uppercase tracking-[0.16em] text-text-muted",
                  "transition-colors hover:text-text-primary",
                )}
              >
                {bundle.nav[id]}
              </a>
            ))}
          </nav>
        </div>
      )}

      {/* Macro #33 · W7 — DRAFT legal / privacy surfaces (no trackers, no
          pixels, no cookie banner). Mobile keeps the compact disclosure
          (Acceptance Fix — footer polish, Claudio). */}
      {!isDesktop && <LegalDisclosure />}
    </footer>
  );
}

/* Compact "Legal & Policies" disclosure. Collapsed by default; expands to the
 * full LOCALIZED legal index. Desktop keeps the left-aligned footer language;
 * mobile is centred, balanced, with ≥36px touch targets and no overflow. */
function LegalDisclosure() {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState(false);
  const bundle = getLegalBundle(locale);
  return (
    <div className="max-w-[1440px] mx-auto mt-6 flex flex-col items-center md:items-start">
      <button
        type="button"
        data-testid="footer-legal-toggle"
        aria-expanded={open}
        aria-controls="footer-legal-nav"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "group inline-flex items-center gap-2 rounded-pill border border-border-soft",
          "px-3.5 py-2 min-h-[36px]",
          "font-mono text-[10px] uppercase tracking-[0.22em] text-text-secondary",
          "transition-colors hover:text-text-primary hover:border-border-strong",
        )}
      >
        <span aria-hidden className="h-1.5 w-1.5 rotate-45 bg-neon-teal/70" />
        {t("home.footer.legalToggle")}
        <svg
          width="11"
          height="11"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden
          className={cn("transition-transform duration-200", open ? "rotate-180" : "rotate-0")}
        >
          <path
            d="M2.5 4.5 6 8l3.5-3.5"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <nav
        id="footer-legal-nav"
        data-testid="footer-legal-nav"
        aria-label={t("home.footer.legalAria")}
        hidden={!open}
        className={cn(
          open ? "grid" : "hidden",
          "mt-4 w-full max-w-[760px]",
          "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4",
          "gap-x-5 gap-y-1 justify-items-center md:justify-items-start",
        )}
      >
        {LEGAL_DOC_ORDER.map((id) => (
          <a
            key={id}
            data-testid={`footer-legal-${id}`}
            href={`#legal/${id}`}
            className={cn(
              "inline-flex items-center min-h-[36px] no-underline",
              "font-mono text-[10px] uppercase tracking-[0.16em] text-text-muted",
              "transition-colors hover:text-text-primary",
            )}
          >
            {bundle.nav[id]}
          </a>
        ))}
      </nav>
    </div>
  );
}

/* ─────────────────── Shared building blocks ──────────────────── */

export function SectionOverline({
  number,
  label,
}: {
  number: string;
  label: string;
}) {
  return (
    <div className="inline-flex items-center gap-3">
      <span
        aria-hidden
        className="h-2 w-2 rotate-45 bg-neon-teal shadow-glow-teal-sm"
      />
      <span className="font-mono font-semibold tracking-[0.28em] uppercase text-[10px] text-text-secondary">
        {number}
      </span>
      <span aria-hidden className="h-3 w-px bg-border-divider" />
      <span className="font-display font-semibold tracking-[0.22em] uppercase text-[10px] text-neon-teal">
        {label}
      </span>
    </div>
  );
}
